<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Message from 'primevue/message';

import { useAdminSession, useCurrentAuction, useToast } from '@/shell';
import { useSettlementStore } from '../store';
import { useSettlementActionToast } from '../action-toast';
import { ReassignMode, type ReassignModeValue } from '../item-actions';
import { contextLine } from '../reassign-consequences';
import { ReassignAddress, firstNameOf, type ReassignSubject, type TargetRow } from '../reassign-target';
import ReassignTargetPool from './reassign/ReassignTargetPool.vue';
import ReassignModeChoice from './reassign/ReassignModeChoice.vue';
import ReassignConsequences from './reassign/ReassignConsequences.vue';
import {
    LotKindSchema, PlacementPriceBasisSchema, PlacementCollectSchema,
} from '@neon-one/auctions-types';
import type {
    PlacementPriceBasis, PlacementCollect,
} from '@neon-one/auctions-types';
import type { PlacementOutcome } from '../types';
import { buildPlacementArgs, describePlacement, placementToast } from './reassign-placement';
import ReassignPriceChoice from './reassign/ReassignPriceChoice.vue';
import ReassignCollectChoice from './reassign/ReassignCollectChoice.vue';

export type { ReassignSubject };

// The dialog is a teleported overlay, so a scoped class never reaches it: the
// width is set inline, which is the convention for sizing a PrimeVue Dialog.
const DIALOG_WIDTH = 'min(30rem, calc(100vw - var(--app-space-32)))';

const props = withDefaults(
    defineProps<{ open: boolean; subject: ReassignSubject | null; initialMode?: ReassignModeValue }>(),
    { initialMode: ReassignMode.SendOffer },
);
const emit = defineEmits<{ 'update:open': [boolean]; done: [message: string] }>();

const store = useSettlementStore();
const flags = useAdminSession();
const auction = useCurrentAuction();
// Archived: no bidder site to accept an offer or a checkout link at, and no
// bidder messages beyond receipts. Placement is direct, and payment is either a
// charge to the saved card or taken later from the Console.
const archived = computed(() => !!auction.event?.archivedAt);
const PriceBasis = PlacementPriceBasisSchema.enum;
const Collect = PlacementCollectSchema.enum;
const RESULT_TOAST_LIFE = 6000;
const toast = useToast();
const actionToast = useSettlementActionToast();

// "item" or "bundle", for every sentence here that names the lot.
const noun = computed(() => props.subject?.lotRef.kind ?? LotKindSchema.enum.item);
const unsold = computed(() => props.subject?.address === ReassignAddress.Lot);
const canPlace = computed(() => unsold.value ? flags.sellToBidder : flags.moveToNextBidder);
const enhanced = computed(() => !!flags.sellToBidder && (unsold.value || !!flags.moveToNextBidder));
// The server's eligibility already accounts for the auction's charge mode. The
// settlement view mode does not: an allow-pay-later auction still auto-charges.
const canCharge = computed(() => !!selected.value?.cardLabel
    && selected.value?.chargeEligibility?.eligible === true);

const openingMode = (): ReassignModeValue => archived.value ? ReassignMode.ReassignNow : props.initialMode;
const mode = ref<ReassignModeValue>(openingMode());
const notify = ref(!archived.value);
const selected = ref<TargetRow | null>(null);
const priceBasis = ref<PlacementPriceBasis>(PriceBasis.custom);
const customCents = ref<number | null>(null);
// What happens without a card to charge: a checkout email, or nothing (archived).
const fallbackCollect = computed<PlacementCollect | undefined>(() => archived.value ? undefined : Collect.notify);
const collect = ref<PlacementCollect | undefined>(fallbackCollect.value);
const collectionTarget = computed(() => selected.value
    ? { ...selected.value, chargeEligibility: selected.value.chargeEligibility ?? undefined } : null);
const effectiveCollect = computed(() => canCharge.value ? collect.value : fallbackCollect.value);
const priceCents = computed(() => {
    if (priceBasis.value === PriceBasis.bid) return selected.value?.bidCents ?? null;
    if (priceBasis.value === PriceBasis.fmv) return props.subject?.fmvCents ?? null;
    return customCents.value;
});
const validPrice = computed(() => priceCents.value !== null
    && Number.isSafeInteger(priceCents.value) && priceCents.value > 0);
const canSubmit = computed(() => canPlace.value && !!selected.value && !submitting.value
    && (!enhanced.value || validPrice.value));
const header = computed(() => unsold.value
    ? mode.value === ReassignMode.SendOffer ? 'Offer to bidder' : 'Sell to bidder'
    : `Reassign ${noun.value}`);

const firstName = computed(() => firstNameOf(selected.value?.name));

const context = computed(() =>
    props.subject
        ? contextLine({
                lot: props.subject.lot,
                itemTitle: props.subject.itemTitle,
                holderName: props.subject.holderName,
                amountCents: props.subject.amountCents,
                unsold: unsold.value,
                hadBids: props.subject.amountCents > 0,
            })
        : '',
);

// ── Submit ───────────────────────────────────────────────────────────────

const submitKey = ref<string | null>(null);
const submitting = ref(false);

function newKey(): string {
    return globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `key-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Lot identity matters for never-invoiced subjects: every one has a null line id.
watch([() => props.open, () => props.subject, archived], ([open]) => {
    if (!open) return;
    mode.value = openingMode();
    notify.value = !archived.value;
    selected.value = null;
    priceBasis.value = PriceBasis.custom;
    customCents.value = null;
    collect.value = fallbackCollect.value;
    submitKey.value = null;
    submitting.value = false;
}, { immediate: true });
watch(selected, (row) => {
    priceBasis.value = row?.bidCents !== null && row?.bidCents !== undefined ? PriceBasis.bid
        : (props.subject?.fmvCents ?? 0) > 0 ? PriceBasis.fmv : PriceBasis.custom;
    customCents.value = null;
    collect.value = canCharge.value ? Collect.charge : fallbackCollect.value;
});
watch([selected, mode, notify, priceBasis, customCents, collect, enhanced], () => {
    submitKey.value = null;
});
const primaryLabel = computed(() => {
    if (unsold.value) return mode.value === ReassignMode.SendOffer
        ? `Offer to ${firstName.value}` : `Sell to ${firstName.value}`;
    return mode.value === ReassignMode.SendOffer
        ? `Send offer to ${firstName.value}` : `Reassign to ${firstName.value}`;
});

// The dialog as it stood when the admin confirmed. The refresh a placement
// triggers re-addresses an Unsold lot by its new invoice line, so reading the
// live computeds afterwards would report a sale as a reassignment.
interface SubmittedAs { unsold: boolean; enhanced: boolean }

function reportFailure(conflict: boolean, itemTitle: string, submittedAs: SubmittedAs): void {
    // Not a retry of the same action: the lot moved, so the next submit is a
    // decision about different state and needs its own key.
    if (conflict) submitKey.value = null;
    actionToast.reportFailure(
        {
            summary:
                mode.value === ReassignMode.SendOffer
                    ? 'Could not send the offer'
                    : `Could not ${submittedAs.unsold ? 'sell the' : 'reassign'} ${noun.value}`,
            detail: 'Nothing was changed. Try again.',
        },
        itemTitle,
    );
}

function reportSuccess(row: TargetRow, outcome: PlacementOutcome, submittedAs: SubmittedAs): string {
    const invoiceNumber = store.invoices.find((invoice) =>
        invoice.invoiceUuid === outcome.result?.newInvoiceUuid)?.invoiceNumber ?? null;
    if (submittedAs.enhanced && mode.value === ReassignMode.ReassignNow) {
        const { severity, summary, detail } = placementToast(row, outcome, {
            unsold: submittedAs.unsold, priceCents: priceCents.value, invoiceNumber, collect: collect.value,
        });
        toast[severity]({ summary, detail, life: RESULT_TOAST_LIFE });
        return detail;
    }
    const detail = outcome.result ? describePlacement(row, outcome.result, invoiceNumber)
        : `The ${noun.value} was placed with ${row.name}.`;
    toast.success({
        summary: mode.value === ReassignMode.SendOffer ? `Offer sent to ${row.name}` : `Reassigned to ${row.name}`,
        detail, life: RESULT_TOAST_LIFE,
    });
    return detail;
}

async function submit(): Promise<void> {
    const subject = props.subject;
    const row = selected.value;
    if (!subject || !row || !canSubmit.value) return;
    submitKey.value ??= newKey();
    const submittedAs: SubmittedAs = { unsold: unsold.value, enhanced: enhanced.value };
    submitting.value = true;
    let outcome: PlacementOutcome;
    try {
        outcome = await store.submitPlacement(buildPlacementArgs(subject, row, {
            mode: mode.value, notify: notify.value, idempotencyKey: submitKey.value, enhanced: submittedAs.enhanced,
            priceBasis: priceBasis.value, priceCents: priceCents.value, collect: effectiveCollect.value,
        }));
    } catch {
        reportFailure(false, subject.itemTitle, submittedAs);
        return;
    } finally {
        submitting.value = false;
    }
    if (!outcome.ok) {
        reportFailure(outcome.conflict, subject.itemTitle, submittedAs);
        return;
    }
    const message = reportSuccess(row, outcome, submittedAs);
    emit('update:open', false);
    emit('done', message);
}
</script>

<template>
    <Dialog
        :visible="open"
        modal
        :header="header"
        :style="{ width: DIALOG_WIDTH }"
        data-testid="reassign-dialog"
        @update:visible="emit('update:open', $event)"
    >
        <div
            v-if="subject"
            class="ri"
        >
            <!-- 1. Which lot, whose it is, and what it went for. -->
            <p
                class="ri__context text-body-sm"
                data-testid="ri__context"
            >
                {{ context }}
            </p>

            <!-- 2–3. The pool, and the target rows inside it. -->
            <ReassignTargetPool
                v-model="selected"
                :open="open"
                :line-id="subject.lineId"
                :lot-ref="subject.lotRef"
                :fmv-cents="subject.fmvCents"
                :line-amount-cents="subject.amountCents"
                :mode="mode"
                :unsold="unsold"
                :enhanced="enhanced"
            />

            <Message
                v-if="archived"
                severity="info"
                :closable="false"
                data-testid="ri__archived-note"
            >
                Neither bidder is notified while this auction is archived. Let them know yourself.
            </Message>

            <!-- 4–5. How the lot is placed, and who hears about it. -->
            <ReassignModeChoice
                :mode="mode"
                :notify="notify"
                :noun="noun"
                :unsold="unsold"
                :enhanced="enhanced"
                :archived="archived"
                @update:mode="mode = $event"
                @update:notify="notify = $event"
            />

            <ReassignPriceChoice
                v-if="enhanced"
                v-model:basis="priceBasis"
                v-model:custom-cents="customCents"
                :selected="selected"
                :fmv-cents="subject.fmvCents"
                :noun="noun"
            />
            <ReassignCollectChoice
                v-if="enhanced && mode === ReassignMode.ReassignNow"
                v-model:collect="collect"
                :selected="collectionTarget"
                :can-charge="canCharge"
                :archived="archived"
            />
            <!-- 6–8. What confirming does, what it costs, and where it lands. -->
            <ReassignConsequences
                :subject="subject"
                :selected="selected"
                :mode="mode"
                :price-cents="priceCents"
                :collect="mode === ReassignMode.ReassignNow ? effectiveCollect ?? null : null"
                :enhanced="enhanced"
            />
        </div>

        <template #footer>
            <Button
                label="Cancel"
                text
                severity="secondary"
                data-testid="ri__cancel"
                @click="emit('update:open', false)"
            />
            <Button
                :label="primaryLabel"
                :disabled="!canSubmit"
                :loading="submitting"
                data-testid="ri__submit"
                @click="submit"
            />
        </template>
    </Dialog>
</template>

<style scoped>
.ri {
    display: flex;
    flex-direction: column;
    gap: var(--app-space-16);
}
.ri__context {
    margin: 0;
    color: var(--app-text-muted);
}
</style>
